import React, { useState, useEffect } from 'react';
import { QrCode, AlertCircle, CheckCircle, Copy, Check, Building2, Download } from 'lucide-react';
import { generatePromptPayQR } from '../utils/promptpayQR';
import SlipUploadPayment from './SlipUploadPayment';

interface PromptPayQRPaymentProps {
  amount: number;
  planName: string;
  planTier: string;
  promptPayPhone: string;
  onPaymentSuccess: (paymentId: string) => void;
  onPaymentError: (error: string) => void;
  additionalData?: any;
}

// Bank account details
const BANK_ACCOUNT = {
  bankName: 'ธนาคารกสิกรไทย',
  bankCode: 'KBANK',
  accountNumber: '024-3-20377-5',
  accountName: 'ธานัท เจริญสุข'
};

const PromptPayQRPayment: React.FC<PromptPayQRPaymentProps> = ({
  amount,
  planName,
  planTier,
  promptPayPhone,
  onPaymentSuccess,
  onPaymentError,
  additionalData
}) => {
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [copiedAccount, setCopiedAccount] = useState(false);

  useEffect(() => {
    const generateQR = async () => {
      try {
        setLoading(true);
        setError('');
        
        // Use PromptPay phone number for QR generation
        // SlipOK will verify the actual bank account from the slip image
        const qrUrl = await generatePromptPayQR(promptPayPhone, amount);
        setQrCodeUrl(qrUrl);
      } catch (err: any) {
        console.error('[PromptPay QR] Error:', err);
        setError(err.message || 'ไม่สามารถสร้าง QR Code ได้');
      } finally {
        setLoading(false);
      }
    };

    generateQR();
  }, [promptPayPhone, amount]);

  const handleCopyAccountNumber = async () => {
    try {
      await navigator.clipboard.writeText(BANK_ACCOUNT.accountNumber.replace(/-/g, ''));
      setCopiedAccount(true);
      setTimeout(() => setCopiedAccount(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleDownloadQR = () => {
    try {
      // Download QR code from data URL
      const link = document.createElement('a');
      link.href = qrCodeUrl;
      link.download = `promptpay-qr-${amount}baht.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Failed to download QR:', err);
    }
  };

  return (
    <div className="space-y-6">
      {/* QR Code Section */}
      <div className="bg-[#EAF6F5] rounded-2xl p-6 border-2 border-[#BEE3DE]">
        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <QrCode className="w-6 h-6 text-[#00B4A1]" />
          สแกน QR Code เพื่อชำระเงิน
        </h3>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-600">กำลังสร้าง QR Code...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-red-900">เกิดข้อผิดพลาด</p>
              <p className="text-sm text-red-800 mt-1">{error}</p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            {/* QR Code Image */}
            <div className="bg-white p-6 rounded-xl shadow-lg mb-4">
              <img
                src={qrCodeUrl}
                alt="PromptPay QR Code"
                className="w-64 h-64"
              />
            </div>

            {/* Payment Info */}
            <div className="text-center space-y-2">
              <p className="text-2xl font-bold text-[#00B4A1]">
                ฿{amount.toLocaleString()}
              </p>
              <p className="text-sm text-gray-600">{planName}</p>
              <p className="text-xs text-gray-500">
                สแกน QR Code ด้วยแอพธนาคารของคุณ
              </p>
            </div>

            {/* Download QR Button */}
            <button
              onClick={handleDownloadQR}
              className="mt-4 px-4 py-2 bg-[#00B4A1] hover:bg-[#0E7C86] text-white rounded-lg font-medium text-sm flex items-center gap-2 transition-colors"
            >
              <Download className="w-4 h-4" />
              บันทึก QR Code
            </button>
          </div>
        )}
      </div>

      {/* Bank Account Details Section */}
      {!loading && !error && (
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border-2 border-green-200">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-green-600" />
            หรือโอนผ่านเลขบัญชี
          </h3>

          <div className="bg-white rounded-xl p-5 space-y-3">
            {/* Bank Name */}
            <div className="flex justify-between items-center pb-3 border-b border-gray-200">
              <span className="text-sm text-gray-600">ธนาคาร</span>
              <span className="font-semibold text-gray-900">{BANK_ACCOUNT.bankName}</span>
            </div>

            {/* Account Number */}
            <div className="flex justify-between items-center pb-3 border-b border-gray-200">
              <span className="text-sm text-gray-600">เลขบัญชี</span>
              <div className="flex items-center gap-2">
                <span className="font-mono font-bold text-gray-900 text-lg">
                  {BANK_ACCOUNT.accountNumber}
                </span>
                <button
                  onClick={handleCopyAccountNumber}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="คัดลอกเลขบัญชี"
                >
                  {copiedAccount ? (
                    <Check className="w-5 h-5 text-green-600" />
                  ) : (
                    <Copy className="w-5 h-5 text-gray-600" />
                  )}
                </button>
              </div>
            </div>

            {/* Account Name */}
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">ชื่อบัญชี</span>
              <span className="font-semibold text-gray-900">{BANK_ACCOUNT.accountName}</span>
            </div>

            {/* Amount */}
            <div className="mt-4 pt-4 border-t-2 border-green-200">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">จำนวนเงินที่ต้องโอน</span>
                <span className="text-2xl font-bold text-green-600">
                  ฿{amount.toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          {/* Copy Success Message */}
          {copiedAccount && (
            <div className="mt-3 flex items-center gap-2 text-sm text-green-700 bg-green-100 px-4 py-2 rounded-lg">
              <CheckCircle className="w-4 h-4" />
              <span>คัดลอกเลขบัญชีแล้ว!</span>
            </div>
          )}

          {/* Info */}
          <p className="text-xs text-gray-600 mt-4 text-center">
            💡 โอนเงินแล้วอย่าลืมอัพโหลดสลิปด้านล่างเพื่อยืนยันการชำระเงิน
          </p>
        </div>
      )}

      {/* Slip Upload Section */}
      {!loading && !error && (
        <SlipUploadPayment
          amount={amount}
          planName={planName}
          planTier={planTier}
          receiverAccount={BANK_ACCOUNT.accountNumber.replace(/-/g, '')}
          onPaymentSuccess={onPaymentSuccess}
          onPaymentError={onPaymentError}
          additionalData={additionalData}
        />
      )}
    </div>
  );
};

export default PromptPayQRPayment;
